import { Fragment, type ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import {
  isNumberedDocumentSectionHeading,
  looksLikeStandaloneAnswerHeading,
} from "../../utils/ragChatUtils";
import { ANSWER_HEADING_SX } from "../../constants/ragUiConstants";

/** Render **bold**, *italic*, simple headings, and bullet lists (no extra dependencies). */
function MarkdownInline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        const bold = part.match(/^\*\*(.+)\*\*$/);
        if (bold) {
          return (
            <Box component="strong" key={i} sx={{ fontWeight: 700 }}>
              {bold[1]}
            </Box>
          );
        }
        const italic = part.match(/^\*([^*]+)\*$/);
        if (italic) {
          return (
            <Box component="em" key={i} sx={{ fontStyle: "italic" }}>
              {italic[1]}
            </Box>
          );
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

function isBulletLine(line: string): boolean {
  return /^[-*•]\s+/.test(line.trim());
}

function isHeadingLine(line: string): boolean {
  return looksLikeStandaloneAnswerHeading(line);
}

function headingText(line: string): string {
  const h = line.match(/^\s*(#{1,6})\s+(.+)$/);
  if (h) return h[2]!.replace(/\*\*/g, "").trim();
  return line.replace(/^\*\*|\*\*$/g, "").replace(/:$/, "").trim();
}

function bulletBody(line: string): string {
  return line.replace(/^[-*•]\s+/, "").trim();
}

/** Display label for `1. **Purpose**` → `1. Purpose` (Typography already bold). */
function numberedSectionHeadingDisplay(line: string): string {
  const m = line.trim().match(/^(\d+)[.)]\s+(.+)$/);
  if (!m) return headingText(line);
  const title = (m[2] ?? "")
    .replace(/^\*\*([^*]+)\*\*$/, "$1")
    .replace(/\*\*/g, "")
    .replace(/:$/, "")
    .trim();
  return `${m[1]}. ${title}`;
}

function renderHeading(key: string, display: string, withTopMargin: boolean): ReactNode {
  return (
    <Typography
      key={key}
      component="h3"
      data-testid="rag-markdown-heading"
      sx={{
        ...ANSWER_HEADING_SX,
        mt: withTopMargin ? 2 : 0,
        mb: 1,
      }}
    >
      <MarkdownInline text={display} />
    </Typography>
  );
}

interface BulletItem {
  title: string;
  continuations: string[];
}

/** Group bullet lines with following non-bullet description lines. */
function groupBulletItems(lines: string[]): {
  leadingProse: string[];
  items: BulletItem[];
} {
  const leadingProse: string[] = [];
  const items: BulletItem[] = [];

  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line === "...") continue;
    if (isBulletLine(line)) {
      items.push({ title: bulletBody(line), continuations: [] });
      continue;
    }
    if (items.length) {
      items[items.length - 1]!.continuations.push(line);
    } else {
      leadingProse.push(line);
    }
  }
  return { leadingProse, items };
}

function renderGroupedBulletList(
  items: BulletItem[],
  key: string,
  dense: boolean
): ReactNode {
  if (!items.length) return null;
  return (
    <Box component="ul" key={key} sx={{ pl: 2.5, my: 1, "& li": { mb: 0.75 } }}>
      {items.map((item, li) => {
        const hasBody = item.continuations.length > 0;
        return (
          <Box component="li" key={li}>
            <Typography
              variant="bodyNormal"
              component="div"
              sx={{
                fontWeight: hasBody ? 700 : 400,
                lineHeight: 1.6,
              }}
            >
              <MarkdownInline text={item.title} />
            </Typography>
            {item.continuations.map((cont, ci) => (
              <Typography
                key={ci}
                variant="bodyNormal"
                component="div"
                sx={{
                  mt: 0.35,
                  mb: dense ? 0.25 : 0.5,
                  lineHeight: 1.6,
                  color: "text.primary",
                  whiteSpace: "pre-wrap",
                }}
              >
                <MarkdownInline text={cont} />
              </Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

function renderBulletList(bulletLines: string[], key: string, dense = false): ReactNode {
  const { items } = groupBulletItems(bulletLines);
  return renderGroupedBulletList(items, key, dense);
}

function renderProseParagraph(
  key: string,
  lines: string[],
  dense: boolean,
  keepNewlines: boolean
): ReactNode {
  if (!lines.length) return null;
  const paragraph = lines.join(keepNewlines || dense ? "\n" : " ");
  return (
    <Typography
      key={key}
      variant="bodyNormal"
      component="div"
      sx={{ whiteSpace: "pre-wrap", mb: dense ? 0.5 : 1.25, lineHeight: 1.6 }}
    >
      <MarkdownInline text={paragraph} />
    </Typography>
  );
}

/**
 * Render body lines after a numbered document section heading:
 * leading prose, then bullets (with optional continuation descriptions).
 */
function renderSectionBody(
  restLines: string[],
  keyPrefix: string,
  dense: boolean
): ReactNode[] {
  const nodes: ReactNode[] = [];
  if (!restLines.length) return nodes;

  const { leadingProse, items } = groupBulletItems(restLines);
  if (leadingProse.length) {
    nodes.push(renderProseParagraph(`${keyPrefix}-prose`, leadingProse, dense, dense));
  }
  if (items.length) {
    nodes.push(renderGroupedBulletList(items, `${keyPrefix}-ul`, dense));
  }
  return nodes;
}

/** Split a block on numbered document-section headings (preserve order). */
function splitAtNumberedSectionHeadings(lines: string[]): string[][] {
  const segments: string[][] = [];
  let current: string[] = [];

  for (const line of lines) {
    if (isNumberedDocumentSectionHeading(line) && current.length > 0) {
      segments.push(current);
      current = [line];
      continue;
    }
    current.push(line);
  }
  if (current.length) segments.push(current);
  return segments;
}

function renderLegacyBlock(
  lines: string[],
  keyPrefix: string,
  dense: boolean,
  headingTopMargin: boolean
): ReactNode[] {
  const nodes: ReactNode[] = [];
  const bulletLines = lines.filter((l) => isBulletLine(l));
  const contentLines = lines.filter((l) => l.trim());
  const allBullets =
    bulletLines.length > 0 && bulletLines.length === contentLines.length;

  if (allBullets) {
    nodes.push(renderBulletList(bulletLines, `${keyPrefix}-b`, dense));
    return nodes;
  }

  const firstLine = lines[0]!;
  const restLines = lines
    .slice(1)
    .map((l) => l.trim())
    .filter((l) => l && l !== "...");
  const restBullets = restLines.filter((l) => isBulletLine(l));
  const restAllBullets =
    restBullets.length > 0 && restBullets.length === restLines.length;

  if (lines.length > 1 && isHeadingLine(firstLine) && restAllBullets) {
    nodes.push(renderHeading(`${keyPrefix}-h`, headingText(firstLine), headingTopMargin));
    nodes.push(renderBulletList(restBullets, `${keyPrefix}-hb`, dense));
    return nodes;
  }

  if (lines.length > 1 && !isBulletLine(firstLine) && restAllBullets) {
    nodes.push(
      <Typography
        key={`${keyPrefix}-intro`}
        variant="bodyNormal"
        component="div"
        sx={{
          whiteSpace: "pre-wrap",
          mb: dense ? 0.25 : 0.75,
          lineHeight: 1.6,
        }}
      >
        <MarkdownInline text={firstLine} />
      </Typography>
    );
    nodes.push(renderBulletList(restBullets, `${keyPrefix}-ib`, dense));
    return nodes;
  }

  // Mixed bullets + continuation descriptions (no numbered section heading).
  if (bulletLines.length > 0 && restLines.some((l) => !isBulletLine(l))) {
    const { leadingProse, items } = groupBulletItems(lines);
    if (leadingProse.length && !isBulletLine(firstLine)) {
      // First line may be a plain intro before mixed list.
      const introEnd = lines.findIndex((l) => isBulletLine(l));
      const intro = (introEnd > 0 ? lines.slice(0, introEnd) : leadingProse).map((l) =>
        l.trim()
      );
      if (intro.length) {
        nodes.push(
          <Typography
            key={`${keyPrefix}-mix-intro`}
            variant="bodyNormal"
            component="div"
            sx={{
              whiteSpace: "pre-wrap",
              mb: dense ? 0.25 : 0.75,
              lineHeight: 1.6,
            }}
          >
            <MarkdownInline text={intro.join(dense ? "\n" : " ")} />
          </Typography>
        );
      }
      const fromBullets = lines.slice(Math.max(introEnd, 0));
      const grouped = groupBulletItems(fromBullets);
      nodes.push(renderGroupedBulletList(grouped.items, `${keyPrefix}-mix-ul`, dense));
      return nodes;
    }
    if (items.length) {
      nodes.push(renderGroupedBulletList(items, `${keyPrefix}-mix-ul`, dense));
      return nodes;
    }
  }

  if (lines.length > 1 && isHeadingLine(firstLine) && restLines.length > 0) {
    nodes.push(renderHeading(`${keyPrefix}-h`, headingText(firstLine), headingTopMargin));
    nodes.push(
      renderProseParagraph(`${keyPrefix}-hp`, restLines, dense, dense)
    );
    return nodes;
  }

  if (lines.length === 1 && isHeadingLine(lines[0]!)) {
    nodes.push(renderHeading(`${keyPrefix}-h`, headingText(lines[0]!), headingTopMargin));
    return nodes;
  }

  const hasBullets = bulletLines.length > 0;
  nodes.push(
    renderProseParagraph(
      `${keyPrefix}-p`,
      lines.map((l) => l.trim()).filter(Boolean),
      dense,
      dense || hasBullets
    )
  );
  return nodes;
}

interface RagMarkdownContentProps {
  text: string;
  /** Preserve single newlines inside paragraphs (e.g. step prose). */
  dense?: boolean;
}

/**
 * Lightweight markdown for assistant answers (bold, headings, bullets).
 * Page citations [p.N] are left intact for upstream parsers.
 */
export function RagMarkdownContent({ text, dense = false }: RagMarkdownContentProps) {
  if (!text.trim()) return null;

  const blocks = dense ? [text] : text.split(/\n\n+/);
  const nodes: ReactNode[] = [];

  blocks.forEach((block, bi) => {
    const lines = block.split("\n").map((l) => l.trimEnd()).filter((l, idx, arr) => {
      if (idx === 0 || l.trim()) return true;
      return arr.slice(idx).some((x) => x.trim());
    });

    if (!lines.length) return;

    // Dense Step Card / compact bodies: keep prior behavior (no section splitting).
    if (dense) {
      nodes.push(...renderLegacyBlock(lines, `d-${bi}`, true, bi > 0));
      return;
    }

    const segments = splitAtNumberedSectionHeadings(lines);
    segments.forEach((segment, si) => {
      const keyPrefix = `b${bi}-s${si}`;
      const first = segment[0]!.trim();
      if (isNumberedDocumentSectionHeading(first)) {
        nodes.push(
          renderHeading(
            `${keyPrefix}-nh`,
            numberedSectionHeadingDisplay(first),
            bi > 0 || si > 0
          )
        );
        const rest = segment
          .slice(1)
          .map((l) => l.trim())
          .filter((l) => l && l !== "...");
        nodes.push(...renderSectionBody(rest, keyPrefix, false));
        return;
      }
      nodes.push(
        ...renderLegacyBlock(segment, keyPrefix, false, bi > 0 || si > 0)
      );
    });
  });

  return <Box>{nodes}</Box>;
}
